'use strict';

import _Object$values from 'babel-runtime/core-js/object/values';
import _Object$keys from 'babel-runtime/core-js/object/keys';
import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import _getIterator from 'babel-runtime/core-js/get-iterator';
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _Object$entries from 'babel-runtime/core-js/object/entries';
import schemas from './schemas';
import Ajv from 'ajv';
import deprecate from 'util-deprecate';
import equals from 'fast-deep-equal';

var ajv = new Ajv();

_Object$entries(schemas).forEach(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      schema = _ref2[1];

  return ajv.addSchema(schema, key);
});

/**
 * Validate a policy.
 * @param {object} policy - the policy to validate
 * @returns {boolean} true iff the policy is valid
 * @throws Error if the policy is invalid
 */
var validate = function validate(policy) {
  var valid = ajv.validate('Policy', policy);

  if (!valid) {
    throw new Error(ajv.errorsText());
  }

  return valid;
};

/**
 * Merge multiple policies into a single policy with the same effect.
 * @param {Array[object]} policies - array of policies to merge
 * @returns {object} the merged policy
 * @throws {Error} if any of the policies is invalid
 */
var merge = function merge(policies) {
  var result = {};

  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = _getIterator(policies), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var policy = _step.value;

      validate(policy);
      _Object$entries(policy.rules).forEach(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 2),
            operation = _ref4[0],
            rules = _ref4[1];

        if (rules === true) {
          // It is safe to ignore the injection attach here because the operation
          // name has been validated by the policy schema before getting this far
          // eslint-disable-next-line security/detect-object-injection
          result[operation] = true;

          // It is safe to ignore the injection attach here because the operation
          // name has been validated by the policy schema before getting this far
          // eslint-disable-next-line security/detect-object-injection
        } else if (result[operation]) {
          // It is safe to ignore the injection attach here because the operation
          // name has been validated by the policy schema before getting this far
          // eslint-disable-next-line security/detect-object-injection
          if (result[operation] !== true) {
            var _result$operation;

            // It is safe to ignore the injection attach here because the operation
            // name has been validated by the policy schema before getting this far
            // eslint-disable-next-line security/detect-object-injection
            (_result$operation = result[operation]).push.apply(_result$operation, _toConsumableArray(rules));
          }
        } else {
          // It is safe to ignore the injection attach here because the operation
          // name has been validated by the policy schema before getting this far
          // eslint-disable-next-line security/detect-object-injection
          result[operation] = [].concat(_toConsumableArray(rules));
        }
      });
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return { rules: result };
};

/**
 * Get a list of all values matching the path (including wildcards).
 * @param {object} attributes - attributes as nested objects
 * @param {array} path - array of path segments
 */
var getAttributeValues = function getAttributeValues(attributes, path) {
  if (attributes === undefined) {
    return [];
  }

  if (path.length === 0) {
    return [attributes];
  }

  var name = path[0];

  switch (name) {
    case '*':
      var keys = _Object$keys(attributes);
      var values = [];

      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = _getIterator(keys), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var key = _step2.value;

          var result = getAttributeValues(attributes[key], path.slice(1));

          // If a single sub-path fails to evaluate fail the entire traversal.
          if (result.length === 0) {
            values = [];
            break;
          } else {
            values = values.concat(result);
          }
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      return values;
    case '%keys':
      return getAttributeValues(_Object$keys(attributes), path.slice(1));
    default:
      var unescapedName = name.replace(/^%%/, '%');
      return getAttributeValues(attributes[unescapedName], path.slice(1));
  }
};

/**
 * Get the attribute value identified by path.
 * @param {object} attributes - attributes as nested objects
 * @param {string} path - string path (e.g. 'user.groups')
 */
var getAttribute = function getAttribute(attributes, name) {
  var path = name.split('.');
  return getAttributeValues(attributes, path)[0];
};

var getCompareValue = function getCompareValue(condition, attributes) {
  if ('target' in condition) {
    return getAttribute(attributes, condition.target);
  } else {
    return condition.value;
  }
};

/**
 * @returns `true` if the comparision matches, `false` if there is a mismatch,
 *          and `undefined` if the target value is not known to compute the
 *          result
 */
var compare = function compare(condition, value, attributes) {
  var compareValue = getCompareValue(condition, attributes);
  switch (condition.comparison) {
    case 'includes':
      if (compareValue === undefined) return undefined;
      return Array.isArray(value) && value.includes(compareValue);

    case 'in':
      if (compareValue === undefined) return undefined;
      return Array.isArray(compareValue) && compareValue.includes(value);

    case 'equals':
      if (compareValue === undefined) return undefined;
      return equals(value, compareValue);

    case 'exists':
      return value !== undefined;

    case 'notEquals':
      if (compareValue === undefined) return undefined;
      return !equals(value, compareValue);

    case 'notIn':
      if (compareValue === undefined) return undefined;
      return Array.isArray(compareValue) && !compareValue.includes(value);

    case 'superset':
      if (compareValue === undefined) return undefined;
      return Array.isArray(value) && Array.isArray(compareValue) && compareValue.every(function (x) {
        return value.includes(x);
      });

    case 'subset':
      if (compareValue === undefined) return undefined;
      return Array.isArray(value) && Array.isArray(compareValue) && value.every(function (x) {
        return compareValue.includes(x);
      });

    default:
      // for unknown comparison types simply deny access:
      return false;
  }
};

var reduceRule = function reduceRule(rule, attributes) {
  var result = {};

  var _iteratorNormalCompletion3 = true;
  var _didIteratorError3 = false;
  var _iteratorError3 = undefined;

  try {
    for (var _iterator3 = _getIterator(_Object$entries(rule)), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
      var _ref5 = _step3.value;

      var _ref6 = _slicedToArray(_ref5, 2);

      var name = _ref6[0];
      var condition = _ref6[1];

      var values = getAttributeValues(attributes, name.split('.'));

      if (values.length === 0) {
        result[name] = condition;
      } else {
        var _iteratorNormalCompletion4 = true;
        var _didIteratorError4 = false;
        var _iteratorError4 = undefined;

        try {
          for (var _iterator4 = _getIterator(values), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
            var value = _step4.value;

            var compareResult = compare(condition, value, attributes);
            if (compareResult === undefined) {
              result[name] = condition;
              break;
            } else {
              if (compareResult === false) {
                return false;
              }
            }
          }
        } catch (err) {
          _didIteratorError4 = true;
          _iteratorError4 = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion4 && _iterator4.return) {
              _iterator4.return();
            }
          } finally {
            if (_didIteratorError4) {
              throw _iteratorError4;
            }
          }
        }
      }
    }
  } catch (err) {
    _didIteratorError3 = true;
    _iteratorError3 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion3 && _iterator3.return) {
        _iterator3.return();
      }
    } finally {
      if (_didIteratorError3) {
        throw _iteratorError3;
      }
    }
  }

  if (_Object$keys(result).length === 0) {
    return true;
  } else {
    return result;
  }
};

var reduceRules = function reduceRules(rules, attributes) {
  var result = [];

  if (rules === true) {
    return true;
  }

  var _iteratorNormalCompletion5 = true;
  var _didIteratorError5 = false;
  var _iteratorError5 = undefined;

  try {
    for (var _iterator5 = _getIterator(rules), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
      var rule = _step5.value;

      var reducedRule = reduceRule(rule, attributes);
      if (reducedRule === true) {
        return true;
      } else if (reducedRule) {
        result.push(reducedRule);
      }
    }
  } catch (err) {
    _didIteratorError5 = true;
    _iteratorError5 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion5 && _iterator5.return) {
        _iterator5.return();
      }
    } finally {
      if (_didIteratorError5) {
        throw _iteratorError5;
      }
    }
  }

  return result;
};

/**
 * Performs a synchronous reduction for whether the given policy might
 * allow the operations.  This function's intended use is for
 * client applications that need a simple check to disable
 * or annotate UI elements.
 * @param {object} policy - the policy to evaluate
 * @param {object} attributes - the attributes to use for the evaluation
 * @returns {object} the policy reduced to conditions involving attributes not not given
 * @throws {Error} if the policy is invalid
 */
var reduce = function reduce(policy, attributes) {
  var result = {};

  validate(policy);
  _Object$entries(policy.rules).forEach(function (_ref7) {
    var _ref8 = _slicedToArray(_ref7, 2),
        operation = _ref8[0],
        rules = _ref8[1];

    rules = reduceRules(rules, attributes);
    if (rules === true || Array.isArray(rules) && rules.length > 0) {
      // It is safe to ignore the injection attach here because the operation name
      // comes from the policy which has been validated already.
      // eslint-disable-next-line security/detect-object-injection
      result[operation] = rules;
    }
  });

  return { rules: result };
};

/**
 * @deprecated use `reduce(...)` instead
 */
var reduceSync = deprecate(reduce, '@lifeomic/abac reduceSync(...) is deprecated. Use reduce(...) instead.');

/**
 * Check whether the given policy allows the operation with the given attributes.
 * @param {string} operation - the requested operation
 * @param {object} policy - the policy to use to check access
 * @param {object} attributes - the attributes to use to check access
 * @returns {boolean} true iff access is allowed, and false otherwise
 * @throws {Error} Error if the policy is invalid
 */
var enforce = function enforce(operation, policy, attributes) {
  try {
    // Before using the policy, make sure it's valid
    validate(policy);
  } catch (error) {
    return false;
  }

  // It is safe to ignore the injection attach here because the operation name has been validated
  // against the allowed operation names
  // eslint-disable-next-line security/detect-object-injection
  var rules = policy.rules && policy.rules[operation] ? policy.rules[operation] : [];
  return reduceRules(rules, attributes) === true;
};

/**
 * Performs a check for whether the given policy might
 * allow the operation.  This function's intended use is for
 * client applications that need a simple check to disable
 * or annotate UI elements. If a rule has not been completely
 * reduced for given operation then this function will assume
 * `true` for the policy evaluation (not safe for server-side
 * enforcement of ABAC policy!).
 *
 * @param {string} operation - the requested operation
 * @param {object} policy - the policy to use to check access
 * @param {object} attributes - the attributes to use to check access
 * @returns {boolean} true iff access is allowed, and false otherwise
 * @throws {Error} Error if the policy is invalid
 */
var enforceLenient = function enforceLenient(operation, policy, attributes) {
  try {
    // Before using the policy, make sure it's valid
    validate(policy);
  } catch (error) {
    return false;
  }

  // It is safe to ignore the injection attach here because the operation name has been validated
  // against the allowed operation names
  // eslint-disable-next-line security/detect-object-injection
  var rules = policy.rules && policy.rules[operation] ? policy.rules[operation] : [];
  var reducedRules = reduceRules(rules, attributes);
  return reducedRules && (reducedRules === true || reducedRules.length > 0);
};

/**
 * @deprecated use `enforceLenient(...)` instead
 */
var enforceSync = deprecate(enforceLenient, '@lifeomic/abac enforceSync(...) is deprecated. Use enforceLenient(...) instead.');

/**
 * Check whether the given policy allows one of a list of operations
 * with the given attributes.
 * @param {string[]} operations - the requested operations
 * @param {object} policy - the policy to use to check access
 * @param {object} attributes - the attributes to use to check access
 * @returns {boolean|string} - the first allowed operation or false
 * @throws {Error} Error if the policy is invalid
 */
var enforceAny = function enforceAny(operations, policy, attributes) {
  var _iteratorNormalCompletion6 = true;
  var _didIteratorError6 = false;
  var _iteratorError6 = undefined;

  try {
    for (var _iterator6 = _getIterator(operations), _step6; !(_iteratorNormalCompletion6 = (_step6 = _iterator6.next()).done); _iteratorNormalCompletion6 = true) {
      var operation = _step6.value;

      if (enforce(operation, policy, attributes)) {
        return operation;
      }
    }
  } catch (err) {
    _didIteratorError6 = true;
    _iteratorError6 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion6 && _iterator6.return) {
        _iterator6.return();
      }
    } finally {
      if (_didIteratorError6) {
        throw _iteratorError6;
      }
    }
  }

  return false;
};

/**
 * Return the list of privileges that the given policy
 * allows against the given attributes.
 * @param {object} policy - the policy to use to check access
 * @param {object} attributes - the attributes to use to check access
 * @returns {string[]} - the list of privileges
 * @throws {Error} Error if the policy is invalid
 */
var privileges = function privileges(policy, attributes) {
  var rules = reduce(policy, attributes).rules;
  return _Object$entries(rules).filter(function (_ref9) {
    var _ref10 = _slicedToArray(_ref9, 2),
        rules = _ref10[1];

    return rules === true;
  }).map(function (_ref11) {
    var _ref12 = _slicedToArray(_ref11, 1),
        privilege = _ref12[0];

    return privilege;
  });
};

/**
 * Synchronously return the list of privileges that the given policy
 * might allow against the given attributes. This function's intended use is for
 * client applications that need a simple check to disable
 * or annotate UI elements. Not safe for server-side!
 * @param {object} policy - the policy to use to check access
 * @param {object} attributes - the attributes to use to check access
 * @returns {string[]} - the list of privileges
 * @throws {Error} Error if the policy is invalid
 */
var privilegesLenient = function privilegesLenient(policy, attributes) {
  var rules = reduce(policy, attributes).rules;
  return _Object$entries(rules).map(function (_ref13) {
    var _ref14 = _slicedToArray(_ref13, 1),
        privilege = _ref14[0];

    return privilege;
  });
};

/**
 * @deprecated use `privilegesLenient(...)` instead
 */
var privilegesSync = deprecate(privilegesLenient, '@lifeomic/abac privilegesSync(...) is deprecated. Use privilegesLenient(...) instead.');

/**
 * Return true iff left is a path prefix of right
 */
var isPathPrefix = function isPathPrefix(left, right) {
  var lhs = left.split('.');
  var rhs = right.split('.');

  if (lhs.length > rhs.length) {
    return false;
  }

  for (var i = 0; i < lhs.length; ++i) {
    if (lhs[i] !== rhs[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Synchronously determines if a given attribute path is used in the rules
 * for a given policy. This may be useful for determining if additional metadata
 * should be fetched before enforcing a policy.
 *
 * @param {object} policy - the policy to check
 * @param {string} attribute - the attribute path, e.g. 'user.patients'
 * @returns {boolean} True if the attribute is in the rules list
 */
var policyRequiresAttribute = function policyRequiresAttribute(policy, attribute) {
  var rules = _Object$values(policy.rules).filter(function (rule) {
    return Array.isArray(rule);
  }).reduce(function (left, right) {
    return left.concat(right);
  }, []);

  var _iteratorNormalCompletion7 = true;
  var _didIteratorError7 = false;
  var _iteratorError7 = undefined;

  try {
    for (var _iterator7 = _getIterator(rules), _step7; !(_iteratorNormalCompletion7 = (_step7 = _iterator7.next()).done); _iteratorNormalCompletion7 = true) {
      var rule = _step7.value;

      for (var key in rule) {
        if (isPathPrefix(attribute, key)) {
          return true;
        } else {
          var target = rule[key].target;
          if (target && isPathPrefix(attribute, target)) {
            return true;
          }
        }
      }
    }
  } catch (err) {
    _didIteratorError7 = true;
    _iteratorError7 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion7 && _iterator7.return) {
        _iterator7.return();
      }
    } finally {
      if (_didIteratorError7) {
        throw _iteratorError7;
      }
    }
  }

  return false;
};

export { validate, merge, reduce, reduceSync /* deprecated */
, enforce, enforceLenient, enforceSync /* deprecated */
, enforceAny, privileges, privilegesLenient, privilegesSync /* deprecated */
, policyRequiresAttribute };
//# sourceMappingURL=index.js.map